import { Node, Edge } from "@xyflow/react";

/**
 * Checks if there is a loop in the Canvas.
 *
 * This function looks at the connections between the nodes and edges in the Canvas
 * to see if there is any circular path that leads back to the starting point.
 */
export const hasCycle = (nodes: Node[], edges: Edge[]): boolean => {
  type AdjacencyList = { [key: string]: string[] };

  const nodeIds = new Set(nodes.map((n) => n.id));

  // Initialize the adjacency list for all nodes
  const adjacencyList: AdjacencyList = nodes.reduce((acc, node) => {
    acc[node.id] = [];
    return acc;
  }, {} as AdjacencyList);

  // Only record edges whose endpoints both exist in `nodes`. After deleting a Zap
  // node or Zap step, callers may pass `newNodes` plus merged edges; stale edges
  // can still point at a removed Zap node or `zap-{parentUuid}-{stepId}` step ids.
  edges.forEach((edge) => {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      adjacencyList[edge.source].push(edge.target);
    }
  });

  const visited = new Set<string>();
  const recStack = new Set<string>();

  const dfs = (node: string): boolean => {
    const neighbors = adjacencyList[node];
    if (neighbors === undefined) {
      return false;
    }

    if (!visited.has(node)) {
      visited.add(node);
      recStack.add(node);

      for (const neighbor of neighbors) {
        if (!visited.has(neighbor) && dfs(neighbor)) {
          return true;
        } else if (recStack.has(neighbor)) {
          return true;
        }
      }
    }
    recStack.delete(node);
    return false;
  };

  // Check each node for cycles
  return nodes.some((node) => dfs(node.id));
};
